import { Role, useClientConfig } from 'pe-core-frontend';
import { useAuth } from './useAuth';
import { useClientIdParam } from './useClientIdParam';
/**
 * Hook to determine the current currency based on the client ID and user information.
 *
 * @param {useCurrentCurrencyProps} param - The parameters for the hook.
 * @param {string} param.clientId - The client ID to lookup the currency for.
 * @returns {string} - The currency code (e.g., 'USD').
 *
 * The hook follows these rules to determine the currency:
 * 1. If `clientId` is provided, it is used for the lookup. This value is from the instance object for which the currency is needed. Ex: PaymentRequest.clientId
 * 2. If `clientId` is not provided, it checks for `clientIdParam` from the URL.
 * 3. If neither `clientId` nor `clientIdParam` is available, it uses the `currentUser`'s client ID.
 * 4. If none of the above are available, it defaults to 'USD'.
 * 5. If the `currentUser`'s client ID matches the lookup client ID, it returns the `currentUser`'s currency.
 * 6. If the `currentUser` has roles like SYS_ADMIN, CLIENT_ADMIN, or CLAIMS_PROCESSOR, it uses the client's currency.
 * 7. If none of the conditions are met, it defaults to 'USD'.
 */
export const useCurrentCurrency = ({ clientId }) => {
    const { currentUser } = useAuth();
    const clientIdParam = useClientIdParam();
    let lookup = clientId;
    if (!lookup) {
        if (clientIdParam) {
            // client admin view
            lookup = clientIdParam;
        }
        else if (currentUser) {
            // global admin or employee view
            lookup = currentUser.clientId;
        }
        else {
            return 'USD';
        }
    }
    if (currentUser && lookup === currentUser.clientId) {
        // only use the current user's currency if it's the same as the client's currency
        return currentUser.clientInfo?.currency || 'USD';
    }
    else if (currentUser?.roleNames &&
        (currentUser.roleNames.includes(Role.SYS_ADMIN) ||
            currentUser.roleNames.includes(Role.CLIENT_ADMIN) ||
            currentUser.roleNames.includes(Role.CLAIMS_PROCESSOR) ||
            currentUser.roleNames.includes(Role.MEMBER_SUPPORT))) {
        // use the client's currency. Current user should have permission to do this
        return useClientConfig({ clientId: lookup }).clientConfig?.currency || 'USD';
    }
    return 'USD';
};
